import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaChevronDown, FaSearch } from "react-icons/fa";
import { usePortalPosition } from "../../TaskManagement/usePortalPosition";
import {
  FILTER_CHEVRON_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  FILTER_TRIGGER_OPEN_CLASS,
  getDropdownOptionStyle,
} from "./commissionsConstants";

export function makeStatusId(groupKey, item) {
  return `${groupKey}::${item.title}::${item.value}`;
}

function CommissionsStatusSelect({
  label = "Status",
  value = [],
  onChange,
  statusGroups = [],
  placeholder = "All Statuses",
  multiple = true,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open, { placement: "bottom", gap: 6 });

  const selectedList = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  const selectedSet = useMemo(
    () => new Set(selectedList.map((item) => item.id)),
    [selectedList],
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setSearch("");
  }, [open]);

  const filterItems = (items) => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (it) => it.title.toLowerCase().includes(q) || String(it.value).includes(q),
    );
  };

  const toggleStatusItem = (groupKey, item) => {
    const id = makeStatusId(groupKey, item);
    const entry = { id, groupKey, title: item.title, value: item.value };

    if (multiple) {
      if (selectedSet.has(id)) {
        onChange(selectedList.filter((s) => s.id !== id));
      } else {
        onChange([...selectedList, entry]);
      }
      return;
    }

    if (selectedSet.has(id)) {
      onChange([]);
    } else {
      onChange([entry]);
    }
    setOpen(false);
    setSearch("");
  };

  const displayLabel = useMemo(() => {
    if (selectedList.length === 0) return placeholder;
    if (selectedList.length === 1) return selectedList[0].title;
    return `${selectedList.length} statuses selected`;
  }, [selectedList, placeholder]);

  const menu = (
    <div
      ref={menuRef}
      className="tailwind-container z-[9999] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
      style={portalStyle}
    >
      <div className="border-b border-gray-100 bg-[#FAFBFC] p-2">
        <div className="relative">
          <FaSearch
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            size={11}
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search status..."
            className="h-[34px] w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-[13px] text-gray-900 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      <div className="max-h-56 overflow-x-hidden overflow-y-auto">
        {statusGroups.map((group) => {
          const items = filterItems(group.items);
          if (items.length === 0) return null;
          return (
            <div key={group.key} className="border-b border-gray-100 p-2 last:border-b-0">
              <div className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {items.map((it) => {
                  const id = makeStatusId(group.key, it);
                  const selected = selectedSet.has(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleStatusItem(group.key, it)}
                      className="flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden text-[13px] transition"
                      style={{
                        ...getDropdownOptionStyle(selected),
                        borderRadius: "6px",
                        padding: "6px 8px",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) e.currentTarget.style.backgroundColor = "#F9FAFB";
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <span className="min-w-0 truncate">{it.title}</span>
                      {multiple && selected && (
                        <FaCheck size={10} className="shrink-0 text-[#007BFF]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-w-0 flex-1 basis-0" ref={containerRef}>
      <label className={FILTER_LABEL_CLASS}>{label}</label>
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((p) => !p)}
          className={`${FILTER_TRIGGER_CLASS} ${open ? FILTER_TRIGGER_OPEN_CLASS : ""} cursor-pointer`}
        >
          <span className={`truncate ${selectedList.length ? "text-gray-800" : "text-gray-400"}`}>
            {displayLabel}
          </span>
          <FaChevronDown
            size={10}
            className={`${FILTER_CHEVRON_CLASS} ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && createPortal(menu, document.body)}
      </div>
    </div>
  );
}

export default CommissionsStatusSelect;
